<script setup lang="ts">
import {contextKey, formatting, legacyModeKey, useContext, ViewContext, ViewOptions} from '@hantera/portal-app';
import {
  ContextMenuButton,
  DropdownDivider,
  DropdownItem,
  FloatingPanel,
  IconHamburger,
  IconPlus,
  TabBar,
  TabItem,
  TextInput
} from '@hantera/design-system';
import {inject, provide, reactive, ref, watchEffect} from 'vue';
import {Pane, paneKey, Tab, TabView} from './workspace-types';
import useWorkspace from '@/composition/useWorkspace';
import NewViewer from './viewers/NewViewer.vue';
import ViewViewer from "./viewers/ViewViewer.vue";
import {v7} from "uuid";

const props = defineProps<{
  pane: Pane
  isLast: boolean
  isFirst: boolean
}>();

const emit = defineEmits<{
  (e: 'addPane'): void
  (e: 'closePane'): void
  (e: 'addTab', tab: Tab): void
  (e: 'tab-activated', tab: Tab): void
}>();

const { state, closeTab, moveTab, getPositionOf, getPaneFor, getTab, openTab, getChildrenOf } = useWorkspace();

const renameTab = ref<Tab | undefined>();
const renameDialog = ref<InstanceType<typeof FloatingPanel>>();
const renameValue = ref('');
const renameInput = ref<InstanceType<typeof TextInput>>();
const legacyMode = inject(legacyModeKey, false);

provide(paneKey, props.pane);

const context = useContext()
const currentViewContext = ref<ViewContext>()

watchEffect(() => {
  const tab = getTab(props.pane.selectedTabId)
  currentViewContext.value = context.getView(tab?.tabId)
})

provide(contextKey, {
  ...context,
  openView(uri: string, options?: ViewOptions): ViewContext {
    const [path, querystring] = uri.split('?')

    const query = options?.query ?? {}
    if (querystring) {
      const params = new URLSearchParams(querystring)
      params.forEach((val, key) => {
        query[key] = val
      })
    }
    
    const tab = <TabView>{
      type: 'view',
      tabId: v7(),
      path,
      query,
      state: reactive(options?.state ?? {}),
      parentTabId: options?.parentViewId
    }
    
    openTab(props.pane.paneId, tab)
    
    return context.getView(tab.tabId)!
  },
})



function viewerFor(tab: Tab) {
  if (tab.type === 'view') {
    return ViewViewer;
  }
  
  console.error(`Unknown tab type: ${tab.type}`);
  closeTab(tab.tabId);
}

function moveLeft(tabId: string) {
  var targetPos = getPositionOf(tabId) - 1;
  moveTab(tabId, targetPos);
}
function moveRight(tabId: string) {
  var targetPos = getPositionOf(tabId) + 1;
  moveTab(tabId, targetPos);
}
function paneLeft(tabId: string) {
  const currentPane = getPaneFor(tabId);
  const tab = getTab(tabId);

  const targetPaneIdx = state.panes.findIndex(r => r.paneId === currentPane?.paneId) - 1;
  const targetPane = state.panes[targetPaneIdx];
  if (!currentPane || !tab || !targetPane) { return; }

  moveTab(tabId, targetPane.tabs.length, targetPane.paneId);

  if (tab.parentTabId) {
    // Save target pane for parent for future child tabs
    const parent = getTab(tab.parentTabId);
    if (parent) {
      parent.childTargetPaneId = targetPane.paneId;
    }
  }
}
function paneRight(tabId: string) {
  const currentPane = getPaneFor(tabId);
  const tab = getTab(tabId);

  const targetPaneIdx = state.panes.findIndex(r => r.paneId === currentPane?.paneId) + 1;
  const targetPane = state.panes[targetPaneIdx];
  if (!currentPane || !tab || !targetPane) { return; }

  moveTab(tabId, targetPane.tabs.length, targetPane.paneId);

  if (tab.parentTabId) {
    // Save target pane for parent for future child tabs
    const parent = getTab(tab.parentTabId);
    if (parent) {
      parent.childTargetPaneId = targetPane.paneId;
    }
  }
}

function rename(idx: number) {
  renameTab.value = props.pane.tabs[idx];
  renameValue.value = renameTab.value.label || '';
  renameDialog.value?.openModal();
  setTimeout(() => {
    renameInput.value?.select();
  }, 1)
}
function saveRename() {
  if (renameTab.value) {
    renameTab.value.label = renameValue.value;
  }
  renameDialog.value?.close();
}
function paneClassesFor(tabId: string) {
  const result = ['pane__view'];
  if (props.pane.selectedTabId === tabId) {
    result.push('pane__view--active');
  }
  return result;
}

function activate() {
  state.activePaneId = props.pane.paneId;
}

function showSidebar() {
  state.showSidebar = true;
}
</script>

<template>
  <div
    class="pane"
    v-bind="$attrs"
    @click="activate"
  >
    
    <TabBar v-model="pane.selectedTabId">
      <template v-slot:actions>
        <button class="btn-silent show-sidebar-button" v-if="isFirst && !state.showSidebar" @click="showSidebar">
          <IconHamburger />
        </button>
      </template>
      <template v-slot:default>
        <TabItem
          v-for="(tab, idx) in pane.tabs"
          :key="tab.tabId"
          :label="(tab.hasUnsavedChanges ? '*' : '') + (tab.label ? tab.label + (tab.count ? ` (${formatting.number(tab.count)})` : '') : 'Loading...')"
          :value="tab.tabId"
          @close="closeTab(tab.tabId)"
          :class="{
            'tab--child': typeof tab.parentTabId === 'string',
            'tab--active': state.activePaneId === pane.paneId && pane.selectedTabId === tab.tabId
          }"
        >
          <DropdownItem
            v-if="idx > 0"
            @click="moveLeft(tab.tabId)"
          >
            Move left
          </DropdownItem>
          <DropdownItem
            v-if="idx < pane.tabs.length - 1"
            @click="moveRight(tab.tabId)"
          >
            Move right
          </DropdownItem>
          <DropdownItem
            v-if="state.panes.findIndex(r => r.paneId === pane.paneId) > 0"
            @click="paneLeft(tab.tabId)"
          >
            Move to left pane
          </DropdownItem>
          <DropdownItem
            v-if="state.panes.findIndex(r => r.paneId === pane.paneId) < state.panes.length - 1"
            @click="paneRight(tab.tabId)"
          >
            Move to right pane
          </DropdownItem>
          <DropdownDivider />
          <DropdownItem @click="closeTab(tab.tabId)">
            Close tab
          </DropdownItem>
        </TabItem>
        <TabItem
          value="new"
        >
          <template v-slot:content>
            <IconPlus class="new-tab-label"/>
          </template>
        </TabItem>
      </template>
      <template v-slot:filler>
        <ContextMenuButton class="pane__context-menu">
          <DropdownItem @click="emit('addPane')">
            Add pane to the right
          </DropdownItem>
          <DropdownItem
            @click="emit('closePane')"
            v-if="state.panes.length > 1"
          >
            Close this pane
          </DropdownItem>
        </ContextMenuButton>
      </template>
    </TabBar>

    <div
      v-for="tab in pane.tabs"
      :key="tab.tabId"
      :class="paneClassesFor(tab.tabId)"
    >
      <Suspense>
        <component
          :is="viewerFor(tab)"
          :view-context="context.getView(tab.tabId)!"
          :tab="(tab as any)"
          :is-selected="pane.selectedTabId === tab.tabId"
          :is-active="state.activePaneId === pane.paneId && pane.selectedTabId === tab.tabId"
        />
        <template #fallback>
          Loading...
        </template>
      </Suspense>
    </div>
    <div :class="paneClassesFor('new')">
      <NewViewer />
    </div>
  </div>
  <FloatingPanel
    ref="renameDialog"
    label="Rename list"
    :show-close="true"
  >
    <div>
      <TextInput
        v-model="renameValue"
        ref="renameInput"
        @keyenter="saveRename"
      />
    </div>
    <button
      class="btn-primary"
      @click="saveRename"
    >Save</button>
  </FloatingPanel>
</template>

<style scoped>

.show-sidebar-button {
  width: 22px;
  height: 22px;
  padding: 0;
  --btn-border-radius: 4px;
}

.show-sidebar-button svg {
  width: 10px;
}

.new-tab-label {
  margin: 0 -3px;
  width: 12px;
  height: 12px;
}

</style>

<style>
.pane {
  display: flex;
  flex-direction: column;
  background-color: hsl(var(--color-90));
}

.pane__context-menu {
  margin: 0 5px;
  height: 20px;
  width: 20px;
  padding: 0;
}

.pane__view {
  height: calc(100% - 32px);
  overflow: hidden;
  display: none;
}

.pane__view--active {
  display: block;
}

.pane__view--inactive {
  display: none;
}

.tab--child .tab__label {
  font-style: italic;
}

.tab--active {
  border-top: 2px solid hsl(var(--color-primary));
  border-bottom: 2px solid transparent;
}
</style>